import { useAuth } from "../context/AuthContext";
import {
  Box,
  CircularProgress,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery, // added
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SmartScreenOutlinedIcon from "@mui/icons-material/SmartScreenOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import BuildIcon from "@mui/icons-material/Build";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useLanguage from "../hooks/useLanguage";
import { translateControl } from "../assets/variables/controlTranslations";

import { useLogout, useLogoutAllDevices } from "../services/logout";
import logo from "../assets/images/rc-logo.png";
import nettoLogo from "../assets/images/netto-logo-icon.jpg";
import auhLogo from "../assets/images/auh-white.png";
import cphLogo from "../assets/images/cph-logo-whitebg.png";
import bauhausLogo from "../assets/images/bauhaus-logo.png";

// Customer-specific branding configuration
const customerBranding = {
  netto: {
    name: "Netto",
    logo: nettoLogo,
    color: "#ffdc14", // #fad85b", // Netto yellow
    logoBgColor: "#ffffff", // Add background color for better contrast
    textColor: "#000000", // Add text color for better contrast
    iconColor: "#000000", // Add icon color for better contrast
  },
  auh: {
    name: "AUH",
    logo: auhLogo,
    color: "#256575", // AUH teal
    logoBgColor: "#256575", // Use same color as background for better contrast
    textColor: "#ffffff", // Add text color for better contrast
    iconColor: "#ffffff", // Add icon color for better contrast
  },
  cph: {
    name: "CPH",
    logo: cphLogo,
    color: "#060e4d", // CPH blue
    logoBgColor: "#060e4d", // Add background color for better contrast
    textColor: "#ffffff", // Add text color for better contrast
    iconColor: "#ffffff", // Add icon color for better contrast
  },
  bauhaus: {
    name: "Bauhaus",
    logo: bauhausLogo,
    color: "#ed1c24", // Bauhaus red
    logoBgColor: "#ed1c24", // Add background color for better contrast
    textColor: "#ffffff", // Add text color for better contrast
    iconColor: "#ffffff", // Add icon color for better contrast
  },
  // Add more customers as needed
};

// Function to get customer branding based on user
const getCustomerBranding = (user) => {
  // Only show customer branding for customer, technician, and control roles

  if (
    !user ||
    (user.role !== "customer" &&
      user.role !== "technician" &&
      user.role !== "control")
  ) {
    return {
      logo: logo,
      color: "#0ea8e5ff", // Default RC color
      logoBgColor: "#ffffff", // Default logo background color
      textColor: "#ffffff", // Default text color
      iconColor: "#ffffff", // Default icon color
    };
  }

  // Get customer branding based on customerId
  const customerName = user.customerName;
  console.log("Customer Name:", customerName);

  if (customerName) {
    const lowerCustomerName = customerName.toLowerCase();

    // Check if customerName contains netto, bauhaus, auh, or cph.
    // "bauhaus" must be checked before "auh" since it contains that substring.
    let brandingKey = null;
    if (lowerCustomerName.includes("netto")) {
      brandingKey = "netto";
    } else if (lowerCustomerName.includes("bauhaus")) {
      brandingKey = "bauhaus";
    } else if (lowerCustomerName.includes("auh")) {
      brandingKey = "auh";
    } else if (lowerCustomerName.includes("cph")) {
      brandingKey = "cph";
    }

    if (brandingKey) {
      const branding = customerBranding[brandingKey];
      return {
        logo: branding.logo,
        color: branding.color,
        logoBgColor: branding.logoBgColor,
        textColor: branding.textColor,
        iconColor: branding.iconColor,
      };
    }
  }

  // Default branding if no customer match
  return {
    logo: logo,
    color: "#0ea8e5ff", // Default RC color
    textColor: "#ffffff", // Default text color
    logoBgColor: "#ffffff", // Default logo background color
    iconColor: "#ffffff", // Default icon color
  };
};

export default function TopNavBar({ title, heading }) {
  const { user } = useAuth();
  const userRole = user?.role;
  const isAdmin = userRole === "admin";
  const isUser = userRole === "user";
  const isControl = userRole === "control";
  const isCustomer = userRole === "customer";
  const isTechnician = userRole === "technician";
  const roleIcon = isControl ? (
    <SmartScreenOutlinedIcon fontSize="small" />
  ) : isUser ? (
    <SupportAgentOutlinedIcon fontSize="small" />
  ) : isAdmin ? (
    <AdminPanelSettingsOutlinedIcon fontSize="small" />
  ) : isCustomer ? (
    <SupervisorAccountOutlinedIcon fontSize="small" />
  ) : isTechnician ? (
    <BuildOutlinedIcon fontSize="small" />
  ) : null;
  const navigate = useNavigate();
  const location = useLocation();
  // The app runs in the rc_service_app WebView, which has no browser toolbar,
  // so control users get their own Back and Refresh buttons on the controls pages.
  const isControlsList = location.pathname.replace(/\/+$/, "") === "/device-controls";
  const isControlsDevice = /^\/device-controls\/[^/]+/.test(location.pathname);
  // On the login page nobody is signed in yet (so the role is unknown), but the
  // tablet still needs a way to reload - e.g. after the network comes back.
  const isLoginPage = location.pathname.startsWith("/login");
  const logout = useLogout();
  const logoutAllDevices = useLogoutAllDevices();
  const { isAuth } = useAuth();

  // Reactive branding calculation
  const [branding, setBranding] = useState(() => getCustomerBranding(user));

  useEffect(() => {
    setBranding(getCustomerBranding(user));
  }, [user]);

  const theme = useTheme();

  // remove window-based fallback and IIFE — call hook at top level
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // prefer responsive hook for re-render on resize
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const servicesOpen = Boolean(servicesAnchorEl);
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);
  const accountMenuOpen = Boolean(accountAnchorEl);
  const [language, setLanguage] = useLanguage();

  const toggleLanguage = () => {
    const next = language === "en" ? "da" : "en";
    setLanguage(next);
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleServicesMenuOpen = (event) =>
    setServicesAnchorEl(event.currentTarget);
  const handleServicesMenuClose = () => setServicesAnchorEl(null);
  const handleAccountMenuOpen = (event) =>
    setAccountAnchorEl(event.currentTarget);
  const handleAccountMenuClose = () => setAccountAnchorEl(null);

  const handleDrawerToggle = () => setDrawerOpen((s) => !s);
  const handleNavigate = (path) => {
    navigate(path);
    setDrawerOpen(false);
    handleMenuClose();
    handleServicesMenuClose();
  };

  const accountMenu = (
    <>
      <Tooltip title="Account">
        <IconButton
          sx={{ color: branding.iconColor }}
          onClick={handleAccountMenuOpen}
          aria-controls={accountMenuOpen ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={accountMenuOpen ? "true" : undefined}
        >
          <AccountCircleIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="account-menu"
        anchorEl={accountAnchorEl}
        open={accountMenuOpen}
        onClose={handleAccountMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 220 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {roleIcon}
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {user?.username}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={toggleLanguage} sx={{ gap: 1 }}>
          <LanguageIcon fontSize="small" />
          Language: {language === "da" ? "Dansk" : "English"}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleAccountMenuClose();
            logout();
          }}
        >
          Log out
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleAccountMenuClose();
            logoutAllDevices();
          }}
          sx={{ color: "error.main" }}
        >
          Log out of all devices
        </MenuItem>
      </Menu>
    </>
  );

  // Control users (location tablets) stay signed in through the rc_service_app's
  // installation ID in the refresh token, so their menu has no log out: it shows who
  // is signed in and the language switch (shared with the controls page).
  const tc = (key) => translateControl(language, `nav.${key}`);

  // Big touch targets for fingers on the location tablet (56px, above the 48px minimum)
  const controlButtonSx = {
    color: branding.iconColor,
    width: 56,
    height: 56,
    ml: 0.5,
  };

  const controlAccountMenu = (
    <>
      {isControlsDevice && (
        <Tooltip title={tc("back")}>
          <IconButton
            sx={controlButtonSx}
            onClick={() => navigate("/device-controls")}
            aria-label={tc("back")}
          >
            <ArrowBackIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Tooltip>
      )}
      {(isControlsList || isControlsDevice) && (
        <Tooltip title={tc("refresh")}>
          <IconButton
            sx={controlButtonSx}
            // Full reload: picks up a new app version and re-reads all state from the server
            onClick={() => window.location.reload()}
            aria-label={tc("refresh")}
          >
            <RefreshIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={user?.username || ""}>
        <IconButton
          sx={controlButtonSx}
          onClick={handleAccountMenuOpen}
          aria-label={user?.username || "Control user"}
          aria-controls={accountMenuOpen ? "control-account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={accountMenuOpen ? "true" : undefined}
        >
          <SmartScreenOutlinedIcon sx={{ fontSize: 30 }} />
        </IconButton>
      </Tooltip>
      <Menu
        id="control-account-menu"
        anchorEl={accountAnchorEl}
        open={accountMenuOpen}
        onClose={handleAccountMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 240 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {user?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        {/* Tall row for finger taps; switches every open page via useLanguage */}
        <MenuItem onClick={toggleLanguage} sx={{ gap: 1.5, minHeight: 56 }}>
          <LanguageIcon />
          {tc("language")}: {language === "da" ? tc("danish") : tc("english")}
        </MenuItem>
      </Menu>
    </>
  );

  if (isAuth === null) {
    return (
      <AppBar position="static" sx={{ backgroundColor: "#fff", color: "#222" }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {heading}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CircularProgress color="inherit" size={24} />
          </Box>
        </Toolbar>
      </AppBar>
    );
  }

  const DrawerList = () => (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onKeyDown={() => setDrawerOpen(false)}
    >
      <List>
        {isControl ? (
          <>
            <ListItemButton
              onClick={() => {
                handleNavigate("/device-controls");
              }}
            >
              {" "}
              <ListItemText primary="Device Controls" />
            </ListItemButton>
          </>
        ) : isTechnician ? null : (
          <>
            {isCustomer && (
              <ListItemButton
                onClick={() => {
                  handleNavigate("/performance-dashboard");
                }}
              >
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            )}
            {(isAdmin || isUser) && (
              <>
                <ListItemButton
                  onClick={() => {
                    handleNavigate("/");
                  }}
                >
                  <ListItemText primary="Dashboard" />
                </ListItemButton>
                <ListItemButton
                  onClick={() => {
                    handleNavigate("/customers");
                  }}
                >
                  <ListItemText primary="Customers" />
                </ListItemButton>
              </>
            )}
            {(isAdmin || isTechnician || isUser || isCustomer) && (
              <>
                <ListItemButton disabled>
                  <ListItemText primary="Services" />
                </ListItemButton>
                {(isAdmin || isUser || isCustomer) && (
                  <>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => {
                        handleNavigate("/performance-dashboard");
                      }}
                    >
                      <ListItemText primary="Performance Dashboard" />
                    </ListItemButton>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => {
                        handleNavigate("/driftsdashboard");
                      }}
                    >
                      <ListItemText primary="Driftsdashboard" />
                    </ListItemButton>
                  </>
                )}
                {(isAdmin || isUser) && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleNavigate("/service-dashboard");
                    }}
                  >
                    <ListItemText primary="Service Coordinator App" />
                  </ListItemButton>
                )}
                {isCustomer && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleNavigate("/service-dashboard/" + user.customerPkId);
                    }}
                  >
                    <ListItemText primary="Servicekoordinator" />
                  </ListItemButton>
                )}
                {(isAdmin || isUser || isTechnician || isCustomer) && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleNavigate("/my-services");
                    }}
                  >
                    <ListItemText primary="Serviceassistent App" />
                  </ListItemButton>
                )}
                {isAdmin && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleNavigate("/configure-routes");
                    }}
                  >
                    <ListItemText primary="Configure Routes" />
                  </ListItemButton>
                )}
                {isAdmin && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleNavigate("/configure-service-plans");
                    }}
                  >
                    <ListItemText primary="Configure Service Plans" />
                  </ListItemButton>
                )}
                {isAdmin && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleNavigate("/configure-service-tasks");
                    }}
                  >
                    <ListItemText primary="Configure Service Tasks" />
                  </ListItemButton>
                )}
                {isAdmin && (
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => {
                      handleNavigate("/configure-plan-allocation");
                    }}
                  >
                    <ListItemText primary="Configure Plan Allocation" />
                  </ListItemButton>
                )}
              </>
            )}
            {(isAdmin || isUser) && (
              <ListItemButton
                onClick={() => {
                  handleNavigate("/logs");
                }}
              >
                <ListItemText primary="Search Logs" />
              </ListItemButton>
            )}
          </>
        )}
      </List>
      <Divider />
      <Box sx={{ px: 2, py: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {roleIcon}
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {user?.username}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap>
          {user?.email}
        </Typography>
      </Box>
      <Divider />
      <List>
        {isUser && (
          <>
            <ListItemButton
              onClick={() => {
                handleNavigate("/edit-account");
              }}
            >
              <ListItemText primary="Edit Profile" />
            </ListItemButton>
            <Divider />
          </>
        )}
        {isAdmin && (
          <>
            <ListItemButton
              onClick={() => {
                handleNavigate("/user-management");
              }}
            >
              <ListItemText primary="User Management" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                handleNavigate("/telemetry");
              }}
            >
              <ListItemText primary="TelemetryTesting" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                handleNavigate("/device-connection-status");
              }}
            >
              <ListItemText primary="Connection Status" />
            </ListItemButton>
            <Divider />
          </>
        )}
        <ListItemButton
          onClick={() => {
            setDrawerOpen(false);
            logout();
          }}
        >
          <ListItemText primary="Logout" />
        </ListItemButton>
        <ListItemButton
          onClick={() => {
            setDrawerOpen(false);
            logoutAllDevices();
          }}
        >
          <ListItemText
            primary="Logout of all devices"
            primaryTypographyProps={{ color: "error" }}
          />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="static"
      sx={{ backgroundColor: branding.color, color: branding.textColor }}
    >
      <Toolbar>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: 2,
            backgroundColor: branding.logoBgColor,
          }}
        >
          <img src={branding.logo} alt="Logo" style={{ height: 40 }} />
        </Box>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {heading}
        </Typography>

        {isAuth && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
              {/* Show customer indicator for admin/technician */}
              {(isAdmin || isTechnician) && user?.customer_id && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.25,
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 1,
                    fontSize: "0.7rem",
                  }}
                >
                  {customerBranding[user.customer_id]?.name ||
                    `Customer ${user.customer_id}`}
                </Typography>
              )}
            </Box>

            {isControl ? (
              // Same on every screen size: tablets in portrait would otherwise get the drawer
              controlAccountMenu
            ) : matchesMdUp ? (
              <>
                {isTechnician ? (
                  <>{accountMenu}</>
                ) : (
                  <>
                    {(isAdmin || isUser) && (
                      <Tooltip title="Live Dashboard">
                        <IconButton
                          sx={{ color: branding.textColor }}
                          onClick={() => {
                            handleNavigate("/");
                          }}
                        >
                          <MonitorHeartOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isCustomer && (
                      <>
                        <Tooltip title="Driftsdashboard">
                          <IconButton
                            sx={{ color: branding.iconColor }}
                            onClick={() => handleNavigate("/driftsdashboard")}
                          >
                            <MapOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Performance Dashboard">
                          <IconButton
                            sx={{ color: branding.iconColor }}
                            onClick={() =>
                              handleNavigate("/performance-dashboard")
                            }
                          >
                            <BarChartIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Servicekoordinator">
                          <IconButton
                            sx={{ color: branding.iconColor }}
                            onClick={() =>
                              handleNavigate(
                                "/service-dashboard/" + user.customerPkId,
                              )
                            }
                          >
                            <EventNoteOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Serviceassistent App">
                          <IconButton
                            sx={{ color: branding.iconColor }}
                            onClick={() => handleNavigate("/my-services")}
                          >
                            <AssignmentTurnedInOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {(isAdmin || isUser) && (
                      <Tooltip title="Customers">
                        <IconButton
                          sx={{ color: branding.iconColor }}
                          onClick={() => {
                            handleNavigate("/customers");
                          }}
                        >
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(isAdmin || isUser || isTechnician) && (
                      <Tooltip title="Services">
                        <IconButton
                          sx={{ color: branding.iconColor }}
                          onClick={handleServicesMenuOpen}
                          aria-controls={
                            servicesOpen ? "services-menu" : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={servicesOpen ? "true" : undefined}
                        >
                          <BuildIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(isAdmin || isUser) && (
                      <>
                        <Tooltip title="Search Logs">
                          <IconButton
                            sx={{ color: branding.iconColor }}
                            onClick={() => {
                              handleNavigate("/logs");
                            }}
                          >
                            <TroubleshootIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Settings">
                          <IconButton
                            sx={{ color: branding.iconColor }}
                            onClick={handleMenuOpen}
                            aria-controls={open ? "settings-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? "true" : undefined}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Menu
                      id="services-menu"
                      anchorEl={servicesAnchorEl}
                      open={servicesOpen}
                      onClose={handleServicesMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {(isAdmin || isCustomer) && (
                        <>
                          <MenuItem
                            onClick={() =>
                              handleNavigate("/performance-dashboard")
                            }
                          >
                            Performance Dashboard
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleNavigate("/driftsdashboard")}
                          >
                            Driftsdashboard
                          </MenuItem>
                        </>
                      )}
                      {isAdmin && (
                        <MenuItem
                          onClick={() => handleNavigate("/service-dashboard")}
                        >
                          Service Coordinator App
                        </MenuItem>
                      )}
                      {isCustomer && (
                        <MenuItem
                          onClick={() =>
                            handleNavigate(
                              "/service-dashboard/" + user.customerPkId,
                            )
                          }
                        >
                          Servicekoordinator
                        </MenuItem>
                      )}
                      {(isAdmin || isUser || isTechnician || isCustomer) && (
                        <MenuItem
                          onClick={() => handleNavigate("/my-services")}
                        >
                          Serviceassistent App
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem
                          onClick={() => handleNavigate("/configure-routes")}
                        >
                          Configure Routes
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem
                          onClick={() =>
                            handleNavigate("/configure-service-plans")
                          }
                        >
                          Configure Service Plans
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem
                          onClick={() =>
                            handleNavigate("/configure-service-tasks")
                          }
                        >
                          Configure Service Tasks
                        </MenuItem>
                      )}
                      {isAdmin && (
                        <MenuItem
                          onClick={() =>
                            handleNavigate("/configure-plan-allocation")
                          }
                        >
                          Configure Plan Allocation
                        </MenuItem>
                      )}
                    </Menu>
                    <Menu
                      id="settings-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      {isAdmin && (
                        <>
                          <MenuItem
                            onClick={() => handleNavigate("/user-management")}
                          >
                            User Management
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleNavigate("/telemetry")}
                          >
                            TelemetryTesting
                          </MenuItem>
                          <MenuItem
                            onClick={() =>
                              handleNavigate("/device-connection-status")
                            }
                          >
                            Connection Status
                          </MenuItem>
                        </>
                      )}
                      {isUser && (
                        <MenuItem
                          onClick={() => handleNavigate("/edit-account")}
                        >
                          Edit Account
                        </MenuItem>
                      )}
                    </Menu>
                    {accountMenu}
                  </>
                )}
              </>
            ) : (
              <>
                <IconButton
                  color={branding.textColor}
                  onClick={handleDrawerToggle}
                  edge="end"
                  aria-label="menu"
                >
                  <MenuIcon style={{ color: branding.textColor }} />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                >
                  <DrawerList />
                </Drawer>
              </>
            )}
          </>
        )}

        {!isAuth && isLoginPage && (
          <Tooltip title={tc("refresh")}>
            <IconButton
              sx={controlButtonSx}
              // Full reload: picks up a new app version and retries anything that failed
              onClick={() => window.location.reload()}
              aria-label={tc("refresh")}
            >
              <RefreshIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
    </AppBar>
  );
}
